import useFetch from "hooks/useFetch";
import { requestInquiryRefresh } from "pages/SuperAdminPages/InquiriesPage/inquiries";
import { useRef, useState } from "react";
import { useConfirm, useToast } from "stories/Admin";

interface ApproveResult {
  notice: { id: number; name: string };
  pushSent: boolean;
}

/**
 * «Godkjenn og send varsel», «Godkjenn uten varsel» og «Slett» for opplastinger som venter på godkjenning.
 * Brukes både på Janazah-siden og i AI-loggen. [onDone] kalles etter en vellykket handling.
 */
const usePendingActions = (onDone: () => void) => {
  const { execute, lastError } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const confirm = useConfirm();
  const toast = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const approve = async (id: number, name: string, sendPush: boolean) => {
    setBusyId(id);
    const result = (await executeRef.current(
      `/api/v1/death-notices/admin/${id}/approve?sendPush=${sendPush}`,
      "POST",
    )) as ApproveResult | null;
    setBusyId(null);
    if (!result) {
      toast.error(lastError()?.message ?? `Kunne ikke godkjenne ${name}.`);
      return;
    }
    toast.success(
      result.pushSent
        ? `${name} er publisert, og varsel er sendt.`
        : sendPush
          ? `${name} er publisert (ingen varsel: janazah er passert).`
          : `${name} er publisert uten varsel.`,
    );
    requestInquiryRefresh();
    onDone();
  };

  const remove = async (id: number, name: string) => {
    const ok = await confirm({
      title: `Slette «${name}»?`,
      message: "Den opplastede dødsannonsen slettes og vises aldri i appen.",
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(id);
    // 204 gir tom kropp (null fra execute), så feil leses fra lastError.
    await executeRef.current(`/api/v1/death-notices/admin/${id}`, "DELETE");
    setBusyId(null);
    const error = lastError();
    if (error) {
      toast.error(error.message ?? `Kunne ikke slette ${name}.`);
      return;
    }
    toast.success(`${name} er slettet.`);
    requestInquiryRefresh();
    onDone();
  };

  return { approve, remove, busyId };
};

export default usePendingActions;
